import React, { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { Button } from './ui/button';
import { Sparkles, Menu, X } from 'lucide-react';
import api from '../utils/api';

/**
 * PublicHeader Component
 * Shared header for all public pages (Login, Register, Admin, Landing)
 * Fetches site settings from CMS for logo and branding
 */
const PublicHeader = ({ transparent = false }) => {
  const navigate = useNavigate();
  const [settings, setSettings] = useState(null);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);

  useEffect(() => {
    const fetchSettings = async () => {
      try {
        const response = await api.get('/cms/public/site-settings');
        const processedSettings = { ...response.data };
        const backendUrl = process.env.REACT_APP_BACKEND_URL || '';
        
        // Convert relative URLs to absolute URLs
        if (processedSettings.logo_url && processedSettings.logo_url.startsWith('/api')) {
          processedSettings.logo_url = `${backendUrl}${processedSettings.logo_url}`;
        }
        
        setSettings(processedSettings);
      } catch (error) {
        console.error('Failed to fetch site settings:', error);
      }
    };
    
    fetchSettings();
  }, []);

  const siteName = settings?.site_name || 'OnTrOf';

  return (
    <header className={`border-b border-slate-700/50 backdrop-blur-sm ${transparent ? 'bg-transparent' : 'bg-slate-900/80'}`}>
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex justify-between items-center h-16">
          {/* Logo */}
          <Link to="/" className="flex items-center gap-2">
            {settings?.logo_url ? (
              <img src={settings.logo_url} alt={siteName} className="h-10 w-auto" />
            ) : (
              <div className="w-10 h-10 bg-gradient-to-br from-blue-500 to-cyan-400 rounded-xl flex items-center justify-center">
                <Sparkles className="w-6 h-6 text-white" />
              </div>
            )}
            <span className="text-xl font-bold text-white">{siteName}</span>
          </Link>

          {/* Desktop Navigation */}
          <nav className="hidden md:flex items-center gap-6">
            <Link to="/#features" className="text-sm text-slate-300 hover:text-white transition-colors">
              Features
            </Link>
            <Link to="/#pricing" className="text-sm text-slate-300 hover:text-white transition-colors">
              Pricing
            </Link>
            <Link to="/#contact" className="text-sm text-slate-300 hover:text-white transition-colors">
              Contact
            </Link>
          </nav>

          {/* Auth Buttons */}
          <div className="flex items-center gap-3">
            <Button
              onClick={() => navigate('/register')}
              className="bg-gradient-to-r from-cyan-400 to-blue-500 hover:from-cyan-500 hover:to-blue-600 text-white font-semibold shadow-lg shadow-cyan-500/25"
            >
              Register
            </Button>
            <Button
              variant="outline"
              onClick={() => navigate('/login')}
              className="border-2 border-cyan-400 text-cyan-400 hover:bg-cyan-400 hover:text-slate-900 font-semibold"
            >
              Login
            </Button>
            
            {/* Mobile Menu Toggle */}
            <button
              className="md:hidden text-white p-2"
              onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
            >
              {mobileMenuOpen ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
            </button>
          </div>
        </div>

        {/* Mobile Menu */}
        {mobileMenuOpen && (
          <div className="md:hidden py-4 border-t border-slate-700/50">
            <nav className="flex flex-col gap-4">
              <Link 
                to="/#features" 
                className="text-slate-300 hover:text-white transition-colors"
                onClick={() => setMobileMenuOpen(false)}
              >
                Features
              </Link>
              <Link 
                to="/#pricing" 
                className="text-slate-300 hover:text-white transition-colors"
                onClick={() => setMobileMenuOpen(false)}
              >
                Pricing
              </Link>
              <Link 
                to="/#contact" 
                className="text-slate-300 hover:text-white transition-colors"
                onClick={() => setMobileMenuOpen(false)}
              >
                Contact
              </Link>
            </nav>
          </div>
        )}
      </div>
    </header>
  );
};

export default PublicHeader;
